import type { FC, KeyboardEvent, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ModalProps {
    open: boolean
    onClose: () => void
    labelledBy: string
    variant?: 'center' | 'sheet'
    className?: string
    closeLabel?: string
    children: ReactNode
}

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

// Accessible dialog: traps focus, closes on Escape or backdrop click, locks page scroll and restores focus afterwards.
// Only renders while open, so it never touches the document during server rendering.
export const Modal: FC<ModalProps> = ({
    open,
    onClose,
    labelledBy,
    variant = 'center',
    className,
    closeLabel = 'Close',
    children
}): ReactNode => {
    const panelRef = useRef<HTMLDivElement>(null)
    const onCloseRef = useRef(onClose)

    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    useEffect(() => {
        if (!open) return

        const previouslyFocused = document.activeElement as HTMLElement | null
        const previousOverflow = document.body.style.overflow

        document.body.style.overflow = 'hidden'

        const frame = window.requestAnimationFrame(() => {
            const panel = panelRef.current
            const target = panel?.querySelector<HTMLElement>('[data-autofocus]') ?? panel?.querySelector<HTMLElement>(FOCUSABLE)

            ;(target ?? panel)?.focus()
        })

        const handleKey = (event: globalThis.KeyboardEvent) => {
            if (event.key === 'Escape') onCloseRef.current()
        }

        document.addEventListener('keydown', handleKey)

        return () => {
            window.cancelAnimationFrame(frame)
            document.removeEventListener('keydown', handleKey)
            document.body.style.overflow = previousOverflow
            previouslyFocused?.focus?.()
        }
    }, [open])

    const trapFocus = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Tab' || !panelRef.current) return

        const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(item => item.offsetParent !== null)

        if (items.length === 0) return

        const first = items[0]
        const last = items[items.length - 1]

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault()
            last.focus()
        }
        else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first.focus()
        }
    }

    if (!open) return null

    return createPortal(
        <div className={cn('fixed inset-0 z-[80] flex justify-center', variant === 'sheet' ? 'items-end sm:items-center sm:p-6' : 'items-center p-4 sm:p-6')}>
            <div
                aria-hidden='true'
                onClick={onClose}
                className='fc-fade-up absolute inset-0 bg-[#0e0e0e]/40 backdrop-blur-[3px]'
            />

            <div
                ref={panelRef}
                role='dialog'
                aria-modal='true'
                aria-labelledby={labelledBy}
                tabIndex={-1}
                onKeyDown={trapFocus}
                className={cn(
                    'fc-pop-in relative max-h-[calc(100svh-32px)] w-full overflow-y-auto bg-white text-ink shadow-[0_24px_64px_-24px_rgba(14,14,14,0.45),0_0_0_1px_rgba(14,14,14,0.06)] outline-none',
                    variant === 'sheet'
                        ? 'max-w-full rounded-t-[24px] sm:max-w-[520px] sm:rounded-[24px]'
                        : 'max-w-[520px] rounded-[24px]',
                    className
                )}
            >
                <button
                    type='button'
                    onClick={onClose}
                    aria-label={closeLabel}
                    className='absolute right-4 top-4 z-10 inline-flex size-9 items-center justify-center rounded-full text-slate transition-colors hover:bg-mist hover:text-ink focus-visible:outline-2 focus-visible:outline-violet'
                >
                    <X className='size-[18px]' aria-hidden='true' />
                </button>

                {children}
            </div>
        </div>,
        document.body
    )
}
